import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Building2,
  FileSearch,
  FileText,
  FileType2,
  FolderUp,
  User,
  Users,
} from 'lucide-react'
import { useUser } from '@/hooks/useUser'
import { useDocuments, useWorkspaces } from '@/api/queries'
import type { WorkspaceRead } from '@/api/queries'
import {
  isProcessing,
  MIME_DOCX,
  progressStageLabel,
  type DocumentStatus,
  type DocumentSummary,
} from '@/api/docfiller-types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { SkeletonList } from '@/components/ui/loading-state'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { formatError } from '@/lib/format-error'
import { formatLocalDateTime } from '@/lib/format-date'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

const QUICK_LINKS = [
  {
    to: '/docfiller' as const,
    title: 'Doc Filler',
    description:
      'Upload a PDF or DOCX and the agent extracts and pre-fills the fields for you.',
    icon: FileText,
  },
  {
    to: '/docfiller/chats' as const,
    title: 'Search documents',
    description:
      'Search filenames, fields, filled values, context, and extracted document text.',
    icon: FileSearch,
  },
] as const

const STATUS_TONE: Record<DocumentStatus, React.ComponentProps<typeof Badge>['tone']> = {
  uploaded: 'info',
  parsing: 'info',
  parsed: 'success',
  filling: 'info',
  filled: 'success',
  error: 'danger',
}

const ROLE_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  owner: 'primary',
  admin: 'warning',
  editor: 'success',
  viewer: 'neutral',
}

function DocStatusBadge({ doc }: { doc: DocumentSummary }) {
  const processing = isProcessing(doc.status)
  const label = processing
    ? progressStageLabel(doc.progress_stage)
    : doc.status === 'error' && doc.friendly_error
      ? doc.friendly_error
      : doc.status

  return (
    <Badge
      tone={STATUS_TONE[doc.status]}
      size="xs"
      className="max-w-[14ch] shrink-0 truncate"
    >
      {label}
    </Badge>
  )
}

function RecentDocRow({ doc }: { doc: DocumentSummary }) {
  const Icon = doc.mime_type === MIME_DOCX ? FileType2 : FileText
  const processing = isProcessing(doc.status)
  return (
    <Link
      to="/docfiller/$documentId"
      params={{ documentId: doc.uuid }}
      className="group block rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
      data-testid="dashboard-recent-doc-row"
    >
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5',
          'shadow-soft transition-[box-shadow,border-color] duration-150 ease-out',
          'hover:border-border-strong/70 hover:shadow-raised',
          processing && 'border-primary/30 bg-primary-soft/20',
        )}
      >
        <span
          aria-hidden
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-foreground"
            title={doc.source_filename}
          >
            {doc.source_filename}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatLocalDateTime(doc.created_at)}
          </p>
        </div>
        <DocStatusBadge doc={doc} />
        <ArrowRight className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
      </div>
    </Link>
  )
}

function WorkspaceRow({ workspace }: { workspace: WorkspaceRead }) {
  return (
    <Link
      to="/workspaces"
      className="group block rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
      data-testid="dashboard-workspace-row"
    >
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 shadow-soft transition-[box-shadow,border-color] duration-150 ease-out hover:border-border-strong/70 hover:shadow-raised">
        <span
          aria-hidden
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
        >
          {workspace.is_personal ? (
            <User className="size-4" />
          ) : (
            <Building2 className="size-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {workspace.name}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="size-3" aria-hidden />
            <span>
              {workspace.member_count} member
              {workspace.member_count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <Badge tone={ROLE_TONE[workspace.role] ?? 'neutral'} size="xs">
          {workspace.role}
        </Badge>
        <ArrowRight className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
      </div>
    </Link>
  )
}

export function DashboardPage() {
  const { user } = useUser()
  const {
    data: documents,
    isLoading: docsLoading,
    error: docsError,
  } = useDocuments()
  const {
    data: workspaces,
    isLoading: wsLoading,
    error: wsError,
  } = useWorkspaces()

  const firstName = user?.name?.split(' ')[0]
  const recentDocs = documents?.slice(0, 5) ?? []

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-8 md:px-8">
      <PageHeader
        eyebrow="Workspace"
        title={firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
        description="Pick up where you left off, or start a new fill from the upload surface."
        actions={
          <Button asChild>
            <Link to="/docfiller">
              Upload a document
              <ArrowRight />
            </Link>
          </Button>
        }
      />

      {/* Recent documents */}
      <section className="space-y-3" data-testid="dashboard-recent-docs">
        <SectionHeader
          title="Recent documents"
          description={
            documents && documents.length > 0
              ? `${documents.length} document${documents.length === 1 ? '' : 's'} in your workspace.`
              : undefined
          }
          actions={
            documents && documents.length > 0 ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/docfiller">See all</Link>
              </Button>
            ) : undefined
          }
        />
        {docsLoading && (
          <SkeletonList
            rows={3}
            rowClassName="h-14"
            aria-label="Loading recent documents"
          />
        )}
        {!docsLoading && docsError && (
          <Alert tone="danger" role="alert">
            <AlertDescription>
              Failed to load recent documents: {formatError(docsError)}
            </AlertDescription>
          </Alert>
        )}
        {!docsLoading && !docsError && recentDocs.length === 0 && (
          <EmptyState
            icon={FolderUp}
            title="No documents yet"
            description="Upload a PDF or DOCX to get started."
            action={
              <Button asChild size="sm">
                <Link to="/docfiller">Upload a document</Link>
              </Button>
            }
            data-testid="dashboard-docs-empty"
          />
        )}
        {!docsLoading && !docsError && recentDocs.length > 0 && (
          <ul className="space-y-2" data-testid="dashboard-docs-list">
            {recentDocs.map((doc) => (
              <li key={doc.uuid}>
                <RecentDocRow doc={doc} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Quick access */}
      <section className="space-y-3">
        <SectionHeader
          title="Jump in"
          description="The surfaces you'll touch most. Anything else lives in the sidebar."
        />
        <div className="grid gap-3 md:grid-cols-2">
          {QUICK_LINKS.map(({ to, title, description, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <Card interactive className="aurora-card-hover h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
                    >
                      <Icon className="size-4" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <CardTitle>{title}</CardTitle>
                      <CardDescription>{description}</CardDescription>
                    </div>
                    <ArrowRight className="ml-auto mt-1.5 size-4 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Workspaces */}
      <section className="space-y-3" data-testid="dashboard-workspaces">
        <SectionHeader
          title="Workspaces"
          description="Your shared workspaces and projects."
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to="/workspaces">Manage</Link>
            </Button>
          }
        />
        {wsLoading && (
          <SkeletonList
            rows={2}
            rowClassName="h-14"
            aria-label="Loading workspaces"
          />
        )}
        {!wsLoading && wsError && (
          <Alert tone="danger" role="alert">
            <AlertDescription>
              Failed to load workspaces: {formatError(wsError)}
            </AlertDescription>
          </Alert>
        )}
        {!wsLoading && !wsError && workspaces && workspaces.length === 0 && (
          <EmptyState
            icon={Users}
            title="No workspaces yet"
            description="Create a workspace to collaborate with your team."
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/workspaces">Go to workspaces</Link>
              </Button>
            }
            data-testid="dashboard-workspaces-empty"
          />
        )}
        {!wsLoading && !wsError && workspaces && workspaces.length > 0 && (
          <ul className="space-y-2" data-testid="dashboard-workspaces-list">
            {workspaces.map((ws) => (
              <li key={ws.uuid}>
                <WorkspaceRow workspace={ws} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
